import { useMemo } from 'react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import { FiChevronRight, FiHome } from 'react-icons/fi';
import PropTypes from 'prop-types';

// include any directories we want to filter out from the breadcrumb
const filterKeys = ['logs'];

const DynamicBreadcrumb = ({ homeIcon = <FiHome />, separator = <FiChevronRight />, ...props }) => {
  const location = useLocation();

  const getOriginalSearchParams = (locationState) => {
    if (!locationState) return null;

    // If this location has a 'from'property with search params, check if it also has a 'from'
    if (locationState?.from) {
      // if the 'from' also has a 'from', recursively check that one
      if (locationState.from?.state?.from) {
        const deeperParams = getOriginalSearchParams(locationState.from.state);
        if (deeperParams) return deeperParams;
      }
      // if no deeper params were found, check if this 'from' has search params
      if (locationState.from?.search) {
        return locationState.from.search;
      }
      // no params
      return null;
    }
  };

  const breadcrumbItems = useMemo(() => {
    // Remove leading slash and split by '/'
    const pathSegments = location.pathname.split('/').filter((segment) => segment);
    const filteredPathSegments = pathSegments.filter((segment) => !filterKeys.includes(segment));

    // get the original search params by recursively checking location.state
    const originalSearchParams = getOriginalSearchParams(location.state);

    // Create breadcrumb items from path segments
    return filteredPathSegments.map((segment, index) => {
      // Format the segment for display (capitalize, replace hyphens with spaces)
      const formattedSegment = segment.replace(/_/g, ' ').replace(/(\b\w)|\s+(\w)/g, (match) => match.toUpperCase());
      // Create the base path for this breadcrumb item
      const basePath = `/${filteredPathSegments.slice(0, index + 1).join('/')}`;
      // build query
      let queryString = '';
      if (originalSearchParams) {
        queryString = originalSearchParams;
      } else if (location.search) {
        queryString = location.search;
      } else if (location?.state?.from?.search) {
        queryString = `?${new URLSearchParams(location.state.from.search).toString()}`;
      }

      const path = basePath + (queryString ? queryString : '');

      // Determine if this is the current page
      const isCurrentPage = index === filteredPathSegments.length - 1;

      return {
        name: formattedSegment,
        path,
        isCurrentPage,
      };
    });
  }, [location]);

  return (
    <Breadcrumb fontSize="sm" spacing="8px" separator={separator} color="gray.400" p={4} {...props}>
      {/* Home breadcrumb */}
      <BreadcrumbItem>
        <BreadcrumbLink to="/" as={RouterLink} aria-label="Home">
          {homeIcon}
        </BreadcrumbLink>
      </BreadcrumbItem>

      {/* Dynamic breadcrumbs based on path */}
      {breadcrumbItems.map((item, index) => (
        <BreadcrumbItem key={index} isCurrentPage={item.isCurrentPage}>
          {item.isCurrentPage ? (
            <BreadcrumbLink as="span">{item.name}</BreadcrumbLink>
          ) : (
            <BreadcrumbLink to={item.path} as={RouterLink}>
              {item.name}
            </BreadcrumbLink>
          )}
        </BreadcrumbItem>
      ))}
    </Breadcrumb>
  );
};

DynamicBreadcrumb.propTypes = {
  homeIcon: PropTypes.node,
  separator: PropTypes.node,
};

export default DynamicBreadcrumb;
